import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowsClockwise, CheckCircle, FacebookLogo, InstagramLogo, EnvelopeSimple, ChatText, Article, Image } from "@phosphor-icons/react";

const KINDS = {
  fb: { icon: FacebookLogo, label: "Facebook post", cls: "text-sky-400", dot: "bg-sky-400" },
  ig: { icon: InstagramLogo, label: "Instagram post", cls: "text-pink-400", dot: "bg-pink-400" },
  email: { icon: EnvelopeSimple, label: "Email campaign", cls: "text-emerald-400", dot: "bg-emerald-400" },
  sms: { icon: ChatText, label: "SMS campaign", cls: "text-violet-400", dot: "bg-violet-400" },
  blog: { icon: Article, label: "Blog idea", cls: "text-amber-400", dot: "bg-amber-400" },
  image: { icon: Image, label: "AI image", cls: "text-[#A56CFF]", dot: "bg-[#6B3DFF]" },
};

const FEED = [
  [1, "fb", "Grand kickoff promo — 15% off this week"], [1, "image", "Hero product visual, brand colors"],
  [2, "ig", "Behind the scenes: how we prep every order"], [3, "fb", "Quick tip: 3 signs it's time for maintenance"],
  [4, "email", "Welcome offer for new subscribers"], [5, "ig", "Customer story spotlight"],
  [6, "fb", "Engagement: 'Which one would you pick?'"], [7, "blog", "The complete seasonal checklist"],
  [8, "ig", "Product highlight — best seller feature"], [9, "sms", "Flash reminder: offer ends tonight"],
  [10, "fb", "Educational: what most people get wrong"], [11, "ig", "Team introduction post"],
  [12, "email", "Mid-month value newsletter"], [13, "fb", "Holiday post — weekend celebration"],
  [14, "ig", "Before & after showcase"], [15, "blog", "5 questions customers always ask"],
  [16, "fb", "Seasonal content — getting ready for the change"], [17, "ig", "Quick tip carousel"],
  [18, "sms", "VIP early access to new arrivals"], [19, "fb", "Customer review celebration"],
  [20, "ig", "Engagement: caption this photo"], [21, "email", "Win-back: we miss you + offer"],
  [22, "fb", "Product highlight with launch CTA"], [23, "blog", "How to choose the right option"],
  [24, "ig", "Day-in-the-life reel script"], [25, "fb", "Community shout-out post"],
  [26, "sms", "Last chance: monthly special"], [27, "ig", "Seasonal promo with urgency"],
  [28, "fb", "Educational myth-busting post"], [29, "email", "Month recap + next month preview"],
  [30, "ig", "Milestone celebration post"], [31, "fb", "Strong CTA: book before the rush"],
];

export default function MonthSimulation() {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const [started, setStarted] = useState(false);
  const rootRef = useRef(null);
  const feedRef = useRef(null);
  const done = idx >= FEED.length;

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setStarted(true); io.disconnect(); } }, { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!started || done) return;
    const t = setInterval(() => setIdx((v) => Math.min(v + 1, FEED.length)), 420);
    return () => clearInterval(t);
  }, [started, done]);

  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [idx]);

  const counts = useMemo(() => {
    const c = { social: 0, email: 0, sms: 0, blog: 0, image: 0 };
    FEED.slice(0, idx).forEach(([, k]) => {
      if (k === "fb" || k === "ig") c.social += 1; else c[k] += 1;
    });
    return c;
  }, [idx]);
  const byDay = useMemo(() => {
    const m = {};
    FEED.slice(0, idx).forEach(([d, k]) => { (m[d] = m[d] || []).push(k); });
    return m;
  }, [idx]);
  const secs = ((idx / FEED.length) * 52).toFixed(0);

  return (
    <section ref={rootRef} className="border-y border-white/5 bg-[#0a0a0a]/60" data-testid="month-simulation-section">
      <div className="max-w-6xl mx-auto px-5 py-16 md:py-24">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="label-caps mb-3 brand-text">The Vozelto signature move</div>
          <h2 className="font-head font-extrabold text-3xl md:text-5xl tracking-tight">An entire month of marketing.<br /><span className="brand-text">In under 60 seconds.</span></h2>
          <p className="mt-4 text-[#9a9a9a]">Watch a simulation of a real Vozelto generation run — posts, emails, SMS and blog ideas filling a month, every one waiting for your approval.</p>
        </div>

        <div className="card-matte brand-glow rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-white/8">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <span className={`h-2 w-2 rounded-full ${done ? "bg-emerald-400" : "bg-[#6B3DFF] animate-pulse"}`} />
              {done ? "Month generated" : started ? "AI team generating…" : "Ready"}
              <span className="text-[11px] text-[#7a7a7a] font-normal ml-1 hidden sm:inline">· simulation of a real run</span>
            </div>
            <div className="flex items-center gap-3">
              <span data-testid="simulation-timer" className="font-mono text-sm brand-text font-bold">{secs}s</span>
              {done && (
                <button data-testid="simulation-replay" onClick={() => setIdx(0)} aria-label="Replay"
                  className="h-7 w-7 rounded-full border border-white/10 flex items-center justify-center text-[#9a9a9a] hover:text-white">
                  <ArrowsClockwise size={13} />
                </button>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-5">
            <div ref={feedRef} className="md:col-span-2 h-[300px] md:h-[380px] overflow-hidden p-4 space-y-1.5 bg-[#0c0c0c]" data-testid="simulation-feed">
              {FEED.slice(0, idx).map(([d, k, txt], i) => {
                const K = KINDS[k];
                return (
                  <div key={i} className="flex items-center gap-2 text-xs demo-fade">
                    <CheckCircle size={13} className="text-emerald-400 shrink-0" weight="fill" />
                    <K.icon size={13} className={`${K.cls} shrink-0`} weight="fill" />
                    <span className="text-[#7a7a7a] shrink-0">Day {d}</span>
                    <span className="text-[#cfcfcf] truncate">{txt}</span>
                  </div>
                );
              })}
              {!done && started && <div className="flex items-center gap-2 text-xs text-[#7a7a7a]"><span className="h-3 w-1 bg-[#6B3DFF] animate-pulse" /> writing…</div>}
            </div>

            <div className="md:col-span-3 p-4 md:p-5">
              <div className="grid grid-cols-7 gap-1 mb-3" data-testid="simulation-calendar">
                {Array.from({ length: 31 }).map((_, k) => {
                  const d = k + 1;
                  const kinds = byDay[d] || [];
                  return (
                    <div key={d} className={`h-9 md:h-11 rounded-md border p-1 transition-all duration-300 ${kinds.length ? "bg-[#6B3DFF]/[0.08] border-[#6B3DFF]/25" : "bg-white/[0.03] border-white/8"}`}>
                      <div className="text-[8px] text-[#7a7a7a] leading-none">{d}</div>
                      <div className="flex gap-0.5 mt-1 flex-wrap">
                        {kinds.slice(0, 3).map((kk, j) => <span key={j} className={`h-1.5 w-1.5 rounded-full ${KINDS[kk].dot}`} />)}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[11px] text-[#9a9a9a]" data-testid="simulation-counters">
                <span><b className="text-white">{counts.social}</b> social posts</span>
                <span><b className="text-white">{counts.email}</b> emails</span>
                <span><b className="text-white">{counts.sms}</b> SMS</span>
                <span><b className="text-white">{counts.blog}</b> blog ideas</span>
                <span><b className="text-white">{counts.image}</b> AI images</span>
              </div>
              <div className={`mt-4 transition-all duration-500 ${done ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none"}`}>
                <div className="card-matte border border-[#6B3DFF]/25 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1">
                    <div className="font-head font-bold">Your month is ready for review.</div>
                    <div className="text-xs text-[#9a9a9a] mt-0.5">Nothing publishes until you approve it — then it auto-schedules.</div>
                  </div>
                  <Button data-testid="simulation-start-trial" onClick={() => navigate("/signup")}
                    className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full h-11 px-6 shrink-0">
                    Start Free Trial <ArrowRight size={15} className="ml-1.5" weight="bold" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
